import { applyPlaceEditorialOverride } from './editorialContent';
import {
  PUBLIC_LOCALITY_UNDER_REVIEW_EDITORIAL_LABEL,
  PUBLIC_LOCALITY_UNDER_REVIEW_LABELS,
  catalogCazaLabelOrNeutral,
  getPublicCatalogLocalityPresentation,
  isPublicZghartaCatalogItem,
  normalizeCatalogCaza,
} from '../domain/tourismRules';
import { classifyCatalogImageUrl } from '../domain/googleLiveReferencePolicy';
import { cleanEnglishPublicLocality } from './publicEnglishCatalog';

export const CATALOG_CACHE_KEY = 'zgharta-data:v4';
export const CATALOG_CACHE_MAX_AGE_MS = 86400000;
export const SUPABASE_CONFIG_ERROR_MESSAGE =
  'Supabase not configured — check REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY';
export const LOCALITY_UNDER_REVIEW_LABEL = PUBLIC_LOCALITY_UNDER_REVIEW_LABELS;
export const LOCALITY_UNDER_REVIEW_EDITORIAL_LABEL = PUBLIC_LOCALITY_UNDER_REVIEW_EDITORIAL_LABEL;

const LEGACY_PENDING_APPROVAL_LABEL = 'first-party approved';
const LEGACY_IMPORTED_EVENT_IDS = new Map([
  ['Zgharta Spring Festival::2026-05-28', 'zgharta-spring-festival-2026'],
  ['Mar Mtanios Al-Bedwani Feast and Souk::2026-06-13', 'kfar-zina-mar-mtanios-feast-souk-2026'],
  ['Raskifa Our Lady / Saydeh Feast::2026-08-15', 'raskifa-our-lady-feast-2026'],
]);

function importedCatalogRowId(row) {
  const googlePlaceId = String(row?.google_place_id || '').trim();
  const labels = Array.isArray(row?.editorial_labels) ? row.editorial_labels : [];
  const wasPendingCatalogRow = labels.some(label => (
    String(label).trim().toLowerCase() === LEGACY_PENDING_APPROVAL_LABEL
  ));
  return googlePlaceId && wasPendingCatalogRow ? `pending-${googlePlaceId}` : row.id;
}

function importedEventRowId(row) {
  return LEGACY_IMPORTED_EVENT_IDS.get(`${row?.name || ''}::${row?.event_date || ''}`) || row.id;
}

function catalogCazaFromRow(row) {
  return normalizeCatalogCaza(row?.caza) || undefined;
}

function withCatalogCaza(normalized, row) {
  const caza = catalogCazaFromRow(row);
  if (!caza) return normalized;
  return { ...normalized, caza };
}

export function getDisplayableCatalogImage(imageUrl) {
  const value = typeof imageUrl === 'string' ? imageUrl.trim() : imageUrl;
  if (!value) return undefined;
  return value;
}

export function getDisplayableCatalogWebsite(website) {
  if (typeof website !== 'string') return undefined;
  const value = website.trim();
  if (!value) return undefined;

  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return undefined;
    return parsed.href;
  } catch {
    return undefined;
  }
}

export function getCatalogImageAttribution(imageUrl) {
  const value = typeof imageUrl === 'string' ? imageUrl.trim() : imageUrl;
  if (!value) return null;
  const classification = classifyCatalogImageUrl(value);
  if (classification.status === 'google_place_photo' || classification.status === 'google_hosted_media') {
    return {
      source: classification.status,
      labelEn: 'Google photo',
      labelAr: 'صورة من غوغل',
    };
  }
  return null;
}

// ADR-0004 D2/D4: stored photos are public objects in the `place-photos` bucket,
// served as direct CDN URLs — no signed URL, no Vercel hop, no Google call.
const STORED_PHOTO_BUCKET = 'place-photos';

export function buildStoredPhotoUrl(objectPath) {
  const base = process.env.REACT_APP_SUPABASE_URL;
  if (!base || typeof objectPath !== 'string' || !objectPath.trim()) return undefined;
  return `${base.replace(/\/$/, '')}/storage/v1/object/public/${STORED_PHOTO_BUCKET}/${objectPath.trim()}`;
}

// A dashboard upload (#405) lands as `manual/<table>/<id>/<ts>.<ext>` with a `-thumb`
// sibling written first. The 1600px master is the first-party override in image_url
// (ADR-0004 D4); cards must read the 400px derivative, never the master.
const MANUAL_UPLOAD_RE = new RegExp(`/storage/v1/object/public/${STORED_PHOTO_BUCKET}/manual/[^/?#]+/[^/?#]+/([^/?#]+)\\.([a-z0-9]+)$`, 'i');

export function normalizeManualUploadPhoto(imageUrl) {
  const value = typeof imageUrl === 'string' ? imageUrl.trim() : '';
  const match = value.match(MANUAL_UPLOAD_RE);
  if (!match || /-thumb$/i.test(match[1])) return null;
  const thumb = value.replace(/\.([a-z0-9]+)$/i, '-thumb.$1');
  return { src: value, thumb, attribution: { source: 'first_party_upload' } };
}

function applyDisplayPhotos(normalized, imageUrl, storedPhotos) {
  const manual = normalizeManualUploadPhoto(imageUrl);
  const photos = manual ? [manual, ...storedPhotos] : storedPhotos;
  if (manual) normalized.image = manual.thumb;
  if (photos.length) {
    normalized.photos = photos;
    if (!normalized.image) normalized.image = photos[0].thumb;
  }
}

// `photos` jsonb -> display photos. Element 0 is the hero (ADR-0004 D1); the
// per-photo author/googleMapsUri ride along so the detail page can credit each
// photo individually rather than crediting the row.
export function normalizeStoredPhotos(photos) {
  if (!Array.isArray(photos)) return [];
  return photos
    .filter(photo => !(photo && photo.hidden === true))
    .map(photo => {
      const src = buildStoredPhotoUrl(photo && photo.path);
      if (!src) return null;
      const author = (photo && photo.author) || {};
      return {
        src,
        thumb: buildStoredPhotoUrl(photo.thumb) || src,
        width: photo.width,
        height: photo.height,
        attribution: {
          source: 'stored_google_photo',
          authorName: author.name || undefined,
          authorUri: author.uri || undefined,
          googleMapsUri: photo.googleMapsUri || undefined,
        },
      };
    })
    .filter(Boolean);
}

export function normalizePlaceRow(row) {
  const imageAttribution = getCatalogImageAttribution(row.image_url);
  const normalized = {
    id: importedCatalogRowId(row),
    name: row.name,
    nameAr: row.name_ar,
    category: row.category,
    village: cleanEnglishPublicLocality(row.village) || undefined,
    villageAr: row.village_ar,
    description: row.description,
    descriptionAr: row.description_ar,
    editorNote: row.editor_note,
    editorNoteAr: row.editor_note_ar,
    editorialLabels: row.editorial_labels,
    image: getDisplayableCatalogImage(row.image_url),
    coordinates: { lat: row.latitude, lng: row.longitude },
    openHours: row.open_hours,
    featured: row.featured,
    googlePrimaryType: row.google_primary_type ?? null,
  };
  if (normalized.id !== row.id) normalized.routeId = row.id;
  const storedPhotos = normalizeStoredPhotos(row.photos);
  // ADR-0004 D4 precedence: a hand-picked first-party image_url wins, then the
  // stored hero, then the caller's category gradient fallback. `image` is the
  // card/thumbnail field, so it takes the 400px derivative — the 1200px master
  // is reached through `photos`, which only the detail gallery reads.
  applyDisplayPhotos(normalized, row.image_url, storedPhotos);
  if (imageAttribution) normalized.imageAttribution = imageAttribution;
  if (row.google_place_id) normalized.googlePlaceId = row.google_place_id;
  return applyPlaceEditorialOverride(withCatalogCaza(normalized, row));
}

export function normalizeBusinessRow(row) {
  const imageAttribution = getCatalogImageAttribution(row.image_url);
  const normalized = {
    id: importedCatalogRowId(row),
    name: row.name,
    nameAr: row.name_ar,
    category: row.category,
    village: cleanEnglishPublicLocality(row.village) || undefined,
    villageAr: row.village_ar,
    description: row.description,
    descriptionAr: row.description_ar,
    editorNote: row.editor_note,
    editorNoteAr: row.editor_note_ar,
    editorialLabels: row.editorial_labels,
    image: getDisplayableCatalogImage(row.image_url),
    coordinates: { lat: row.latitude, lng: row.longitude },
    openHours: row.open_hours,
    rating: row.rating,
    priceRange: row.price_range,
    phone: row.phone,
    website: getDisplayableCatalogWebsite(row.website),
    specialties: row.specialties,
    verified: row.verified,
    googlePrimaryType: row.google_primary_type ?? null,
  };
  if (normalized.id !== row.id) normalized.routeId = row.id;
  const storedPhotos = normalizeStoredPhotos(row.photos);
  applyDisplayPhotos(normalized, row.image_url, storedPhotos);
  if (imageAttribution) normalized.imageAttribution = imageAttribution;
  if (row.google_place_id) normalized.googlePlaceId = row.google_place_id;
  return withCatalogCaza(normalized, row);
}

export function normalizeEventRow(row) {
  const normalized = {
    id: importedEventRowId(row),
    name: row.name,
    nameAr: row.name_ar,
    category: row.category,
    village: cleanEnglishPublicLocality(row.village) || undefined,
    description: row.description,
    descriptionAr: row.description_ar,
    date: row.event_date,
    // A multi-day festival's last day. The visitor "past" rule reads `endDate || date`, the same
    // rule `isPastEvent` uses in the dashboard, so a festival stays upcoming until it truly ends.
    endDate: row.end_date,
    time: row.event_time,
    // The manual picture an editor sets in the dashboard (#347 Stage C). Without this the
    // field was stored and then dropped here, so a visitor never saw it — which defeated the
    // whole point of being able to add a picture by hand.
    image: getDisplayableCatalogImage(row.image_url),
    location: cleanEnglishPublicLocality(row.location) || undefined,
    locationAr: row.location_ar,
    featured: row.featured,
    // Ingest provenance, for the detail view's source credit line (#450). Both were stored and
    // then dropped here, so the credit line had nothing to read and the visitor had no way to
    // check a row against its listing. This is the ONLY thing the app takes from a source: the
    // ingest deliberately stores no third-party description, and none is added here.
    source: row.source || undefined,
    sourceUrl: row.source_url || undefined,
  };
  // A dashboard upload is a 1600px master with a 400px `-thumb` sibling (#405/#409). Events
  // have no stored `photos`, so this pair is the only one — but the rule is the same as for
  // places: `image` is the derivative every card renders, and the master rides in
  // `photos[0].src` for the hero. Handing the master to a 44px card thumbnail is the exact
  // egress mistake CLAUDE.md's "photo size per surface" gotcha exists to prevent. A plain
  // first-party link that is not a manual upload has no derivative and is left alone.
  applyDisplayPhotos(normalized, row.image_url, []);
  // Stored `events.caza` only — never inferred from village, name, or coordinates.
  return withCatalogCaza(normalized, row);
}

export function normalizeCatalogRows(rows) {
  return {
    places: (rows.places || []).map(normalizePlaceRow),
    businesses: (rows.businesses || []).map(normalizeBusinessRow),
    events: (rows.events || []).map(normalizeEventRow),
  };
}

export function sanitizeCatalogDisplayFields(catalog) {
  return {
    places: (catalog.places || []).map(place => {
      const imageAttribution = place.imageAttribution || getCatalogImageAttribution(place.image);
      return {
        ...place,
        image: place.image == null ? place.image : getDisplayableCatalogImage(place.image),
        ...(imageAttribution ? { imageAttribution } : {}),
      };
    }),
    businesses: (catalog.businesses || []).map(business => {
      const imageAttribution = business.imageAttribution || getCatalogImageAttribution(business.image);
      return {
        ...business,
        image: business.image == null ? business.image : getDisplayableCatalogImage(business.image),
        website: getDisplayableCatalogWebsite(business.website),
        ...(imageAttribution ? { imageAttribution } : {}),
      };
    }),
    events: catalog.events || [],
  };
}

function toBoundaryCandidate(item) {
  return {
    ...item,
    latitude: item?.coordinates?.lat,
    longitude: item?.coordinates?.lng,
  };
}

function isPublicCatalogItemDisplayable(item) {
  if (hasApprovedPublicCopy(item)) return true;
  return isPublicZghartaCatalogItem(toBoundaryCandidate(item));
}

function appendUniqueEditorialLabel(labels, label) {
  const values = Array.isArray(labels) ? labels.filter(Boolean) : [];
  return values.includes(label) ? values : [...values, label];
}

function toEditorialLabelList(labels) {
  if (Array.isArray(labels)) return labels;
  if (labels == null) return [];
  return [labels];
}

const APPROVED_COPY_LABELS = new Set([
  'approved first-party copy',
  'first-party approved',
]);

const CATEGORY_COPY_LABELS = {
  cafe: 'Cafe',
  heritage: 'Heritage stop',
  hotel: 'Stay',
  nature: 'Nature stop',
  religious: 'Religious stop',
  restaurant: 'Restaurant',
  shop: 'Shop',
  health: 'Health service',
};

function hasApprovedPublicCopy(item) {
  return toEditorialLabelList(item?.editorialLabels)
    .some(label => APPROVED_COPY_LABELS.has(String(label).trim().toLowerCase()));
}

function firstPartyCopyPendingDescription(item) {
  const categoryLabel = CATEGORY_COPY_LABELS[item?.category] || 'Catalog stop';
  const village = item?.catalogLocalityFilterable === false ? null : (item?.village || item?.villageAr);
  const location = village ? ` in ${village}` : ` in ${catalogCazaLabelOrNeutral(item?.caza, 'en')}`;
  return `${categoryLabel}${location}. Description pending first-party confirmation.`;
}

function firstPartyCopyPendingDescriptionAr(item) {
  return `محطة في ${catalogCazaLabelOrNeutral(item?.caza, 'ar')}. الوصف قيد التأكيد من مصدر محلي أو من الجهة المالكة.`;
}

function applyFirstPartyCopyPresentation(item) {
  if (hasApprovedPublicCopy(item)) return item;

  return {
    ...item,
    description: firstPartyCopyPendingDescription(item),
    descriptionAr: firstPartyCopyPendingDescriptionAr(item),
    publicDescriptionPendingFirstPartyConfirmation: true,
  };
}

function applyLocalityReviewPresentation(item) {
  const presentation = getPublicCatalogLocalityPresentation(toBoundaryCandidate(item));
  if (!presentation.needsReview) return item;

  return {
    ...item,
    rawVillage: item.rawVillage || presentation.rawVillage,
    rawVillageAr: item.rawVillageAr || presentation.rawVillageAr,
    village: presentation.village,
    villageAr: presentation.villageAr,
    catalogLocalityNeedsReview: true,
    catalogLocalityFilterable: presentation.filterable,
    catalogLocalityReviewActions: presentation.actions,
    catalogLocalityReviewReasons: presentation.reasons,
    editorialLabels: appendUniqueEditorialLabel(item.editorialLabels, LOCALITY_UNDER_REVIEW_EDITORIAL_LABEL),
  };
}

function filterCatalogForPublicDisplay(catalog) {
  return {
    places: (catalog.places || [])
      .filter(isPublicCatalogItemDisplayable)
      .map(applyLocalityReviewPresentation)
      .map(applyFirstPartyCopyPresentation),
    businesses: (catalog.businesses || [])
      .filter(isPublicCatalogItemDisplayable)
      .map(applyLocalityReviewPresentation)
      .map(applyFirstPartyCopyPresentation),
    events: (catalog.events || [])
      .filter(isPublicCatalogItemDisplayable)
      .map(applyLocalityReviewPresentation),
  };
}

function applyCatalogPresentation(catalog) {
  if (!catalog) return catalog;
  return sanitizeCatalogDisplayFields(filterCatalogForPublicDisplay(catalog));
}

export async function readCatalogRowsFromSupabase(supabaseClient) {
  if (!supabaseClient) {
    throw new Error(SUPABASE_CONFIG_ERROR_MESSAGE);
  }

  const [placesResult, businessesResult, eventsResult] = await Promise.all([
    supabaseClient
      .from('places')
      .select('*')
      .eq('active', true)
      .order('featured', { ascending: false }),
    supabaseClient
      .from('businesses')
      .select('*')
      .eq('active', true)
      .order('rating', { ascending: false }),
    supabaseClient
      .from('events')
      .select('*')
      .order('event_date', { ascending: true }),
  ]);

  if (placesResult.error) throw placesResult.error;
  if (businessesResult.error) throw businessesResult.error;
  if (eventsResult.error) throw eventsResult.error;

  return {
    places: placesResult.data || [],
    businesses: businessesResult.data || [],
    events: eventsResult.data || [],
  };
}

function readFreshCachedCatalog(storage, now) {
  try {
    const cached = storage.getItem(CATALOG_CACHE_KEY);
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    const hasCatalogShape = ['places', 'businesses', 'events']
      .every(key => Array.isArray(parsed[key]));
    const cacheAge = now() - parsed.ts;
    if (hasCatalogShape && cacheAge >= 0 && cacheAge < CATALOG_CACHE_MAX_AGE_MS) {
      return sanitizeCatalogDisplayFields({
        places: parsed.places,
        businesses: parsed.businesses,
        events: parsed.events,
      });
    }
  } catch {}

  return null;
}

function writeCatalogCache(storage, catalog, now) {
  try {
    storage.setItem(CATALOG_CACHE_KEY, JSON.stringify({ ...catalog, ts: now() }));
  } catch {}
}

export async function loadCatalog({
  storage,
  now = Date.now,
  readRows,
  onCacheHit = () => {},
}) {
  const cachedCatalog = readFreshCachedCatalog(storage, now);
  const displayCachedCatalog = applyCatalogPresentation(cachedCatalog);

  if (displayCachedCatalog) {
    onCacheHit(displayCachedCatalog);
  }

  try {
    const rows = await readRows();
    const normalizedCatalog = normalizeCatalogRows(rows);
    const catalog = applyCatalogPresentation(normalizedCatalog);
    writeCatalogCache(storage, normalizedCatalog, now);
    return {
      catalog,
      source: 'fresh',
      hadValidCache: Boolean(displayCachedCatalog),
      error: null,
    };
  } catch (error) {
    return {
      catalog: displayCachedCatalog,
      source: displayCachedCatalog ? 'cache' : 'error',
      hadValidCache: Boolean(displayCachedCatalog),
      error,
    };
  }
}
